import { v4 as uuidv4 } from 'uuid';
import CryptoJS from 'crypto-js';
import { AUTH_CONFIG } from '../config/constants';

/**
 * 登录状态变更事件名。当 token/username/is_admin 发生变化时会派发该事件，
 * 页面内的组件（如顶部导航）可以监听该事件以便实时刷新用户信息，
 * 而不需要整页刷新。
 */
export const AUTH_CHANGE_EVENT = 'auth-changed';

/**
 * 写入登录态到 localStorage，并通知当前页面内所有监听者刷新用户信息
 * @param {{token: string, username?: string, isAdmin?: boolean, userId?: number|string, phone?: string}} session
 */
export const setAuthSession = ({ token, username, isAdmin, userId, phone }) => {
    localStorage.setItem('token', token || '');
    localStorage.setItem('username', username || '');
    localStorage.setItem('is_admin', String(Boolean(isAdmin)));
    if (userId !== undefined && userId !== null) {
        localStorage.setItem('user_id', String(userId));
    }
    if (phone !== undefined && phone !== null) {
        localStorage.setItem('phone', phone || '');
    }
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
};

/**
 * 清除登录态（退出登录 / 登录失效），并通知当前页面内所有监听者刷新用户信息
 */
export const clearAuthSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('is_admin');
    localStorage.removeItem('user_id');
    localStorage.removeItem('phone');
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
};

/**
 * 生成鉴权头部
 * @param {string} method - HTTP 方法 (GET, POST, PUT, DELETE)
 * @param {string} path - API 路径，例如 /api/v2/project/all_business_trees
 * @param {Object} requestParams - 请求参数，用于计算签名
 * @returns {Object} - 鉴权头部对象
 */
export const generateAuthHeaders = (method, path, requestParams = {}) => {
    // 鉴权参数
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const nonce = uuidv4();
    const app_id = AUTH_CONFIG.APP_ID;
    const app_key = AUTH_CONFIG.APP_KEY;
    
    // 确保路径格式正确
    if (!path.startsWith('/api')) {
        path = '/api' + (path.startsWith('/') ? path : '/' + path);
    }
    
    // 创建签名字符串基础部分
    let signatureParams = {
        app_id: app_id,
        method: method.toUpperCase(),
        nonce: nonce,
        path: path,
        timestamp: timestamp
    };
    
    // 对于GET、DELETE请求，将query参数添加到签名中
    if ((method.toUpperCase() === 'GET' || method.toUpperCase() === 'DELETE') && requestParams) {
        // 合并请求参数到签名参数中
        signatureParams = { ...signatureParams, ...requestParams };
    }
    
    // 按键名排序并构建签名字符串
    const sortedKeys = Object.keys(signatureParams).sort();
    const signature_string = sortedKeys.map(key => `${key}=${signatureParams[key]}`).join('&');
    
    console.log('生成签名:', {
        method: method.toUpperCase(),
        path: path,
        timestamp: timestamp,
        nonce: nonce,
        app_id: app_id,
        requestParams: requestParams,
        signature_string: signature_string
    });
    
    // 使用HMAC-SHA256计算签名
    const signature = CryptoJS.HmacSHA256(signature_string, app_key).toString(CryptoJS.enc.Hex);
    
    // 返回鉴权头部
    return {
        'Content-Type': 'application/json',
        'X-App-Id': app_id,
        'X-Timestamp': timestamp,
        'X-Nonce': nonce,
        'X-Signature': signature
    };
};

/**
 * 从URL中提取API路径
 * @param {string} url - 完整URL
 * @returns {string} - API路径部分
 */
export const extractPathFromUrl = (url) => {
    try {
        // 移除协议和域名部分
        let path = url;
        
        if (url.includes('://')) {
            const urlObj = new URL(url);
            path = urlObj.pathname;
        }

        if (path.includes('/api/v1/')) {
            return path.substring(path.indexOf('/api'));
        }
        
        // 处理相对路径如 /api/v1/backend/talent/query
        if (path.startsWith('/api/v1/')) {
            return path;
        }
        
        // 处理相对路径如 /v1/backend/talent/query
        if (path.startsWith('/v1/')) {
            return '/api' + path;
        }
        
        // 处理其他情况，确保以 /api 开头
        if (!path.startsWith('/api')) {
            path = '/api' + (path.startsWith('/') ? path : '/' + path);
        }
        
        console.log('提取的路径:', path);
        return path;
    } catch (error) {
        console.error('URL解析错误:', error);
        // 默认返回原始路径
        return url.startsWith('/') ? url : '/' + url;
    }
};
